import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Divider,
  Popper,
} from "@mui/material";
import { useDebouncedSearch } from "../hooks/useNavBarSearch";
import { Search, X } from "lucide-react";
import { numberFormatter } from "@ecommerce/shared-utils";
import { LG } from "../../../styles/liquidGlass";

interface SearchbarProps {
  onClose?: () => void;
}

export default function Searchbar({ onClose }: SearchbarProps) {
  const navigate = useNavigate();
  const { searchTerm, setSearchTerm, suggestions, loading, isOpen, setIsOpen, clearSearch } =
    useDebouncedSearch();
  const searchRef = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideSearch = searchRef.current?.contains(target);
      const insidePopper = popperRef.current?.contains(target);
      if (!insideSearch && !insidePopper) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  const handleSuggestionClick = (suggestion: any) => {
    navigate(`/productos/${suggestion.sku}`);
    setIsOpen(false);
    setSearchTerm("");
    if (onClose) onClose();
  };

  const handleSeeAllResults = () => {
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    setIsOpen(false);
    setSearchTerm("");
    if (onClose) onClose();
  };

  const displayedSuggestions = suggestions.suggestions.slice(0, 4);
  const showSuggestions = isOpen && (displayedSuggestions.length > 0 || loading);

  return (
    <Box ref={searchRef} sx={{ position: "relative", width: "100%" }}>
      <TextField
        placeholder="Buscar productos..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && searchTerm) {
            e.preventDefault();
            handleSeeAllResults();
          }
        }}
        size="small"
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            height: 36,
            borderRadius: LG.radius.pill,
            background: LG.color.surface,
            backdropFilter: LG.blurLight,
            WebkitBackdropFilter: LG.blurLight,
            fontSize: "0.82rem",
            color: LG.color.text,
            "& fieldset": { borderColor: LG.color.border },
            "&:hover fieldset": { borderColor: LG.color.borderStrong },
            "&.Mui-focused fieldset": { borderColor: LG.color.text, borderWidth: "1px" },
          },
          "& .MuiInputBase-input::placeholder": { color: LG.color.textSecondary, opacity: 1 },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {loading ? (
                <CircularProgress size={16} sx={{ color: LG.color.textSecondary }} />
              ) : searchTerm ? (
                <IconButton onClick={clearSearch} size="small" sx={{ color: LG.color.textSecondary, p: 0.25 }}>
                  <X size={14} />
                </IconButton>
              ) : (
                <Search size={15} color={LG.color.textSecondary} />
              )}
            </InputAdornment>
          ),
        }}
      />

      <Popper
        open={showSuggestions}
        anchorEl={searchRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1400, width: searchRef.current?.clientWidth }}
      >
        <div ref={popperRef}>
        <Paper
          elevation={0}
          sx={{
            maxHeight: 380,
            overflow: "auto",
            borderRadius: LG.radius.md,
            background: "rgba(250, 246, 240, 0.97)",
            backdropFilter: LG.blurStrong,
            WebkitBackdropFilter: LG.blurStrong,
            border: `1px solid ${LG.color.border}`,
            boxShadow: LG.shadow.lg,
            mt: 1,
          }}
        >
          {loading ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <CircularProgress size={20} sx={{ color: LG.color.textSecondary }} />
            </Box>
          ) : (
            <>
              <List sx={{ py: 0.5 }}>
                {displayedSuggestions.map((suggestion) => (
                  <ListItem key={suggestion.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        py: 1,
                        px: 1.5,
                        mx: 0.5,
                        borderRadius: LG.radius.sm,
                        gap: 1.5,
                        "&:hover": { background: LG.color.surface },
                      }}
                    >
                      {suggestion.image && (
                        <Box
                          component="img"
                          src={suggestion.image}
                          alt={suggestion.name}
                          sx={{
                            width: 38,
                            height: 38,
                            objectFit: "cover",
                            borderRadius: LG.radius.xs,
                            flexShrink: 0,
                            border: `1px solid ${LG.color.border}`,
                          }}
                        />
                      )}
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 500, fontSize: "0.82rem", color: LG.color.text }}>
                            {suggestion.name}
                          </Typography>
                        }
                        secondary={
                          suggestion.price && (
                            <Typography sx={{ fontSize: "0.78rem", color: LG.color.textSecondary }}>
                              {numberFormatter.formatPrice(suggestion.price)}
                            </Typography>
                          )
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {suggestions.suggestions.length > 4 && (
                <>
                  <Divider sx={{ borderColor: LG.color.divider }} />
                  <Box sx={{ p: 1.5 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSeeAllResults}
                      sx={{
                        background: LG.color.buttonDark,
                        color: LG.color.buttonDarkText,
                        borderRadius: LG.radius.pill,
                        fontSize: "0.78rem",
                        fontWeight: 500,
                        letterSpacing: "0.06em",
                        py: 0.8,
                        boxShadow: "none",
                        "&:hover": { background: LG.color.text, boxShadow: "none" },
                      }}
                    >
                      VER TODOS LOS RESULTADOS
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>
        </div>
      </Popper>
    </Box>
  );
}
